import React, { useState, useEffect } from 'react';
import { Calendar, ChevronLeft, ChevronRight } from 'lucide-react';
import moment from 'moment';

const MonthSelector = ({ onMonthSelect, selectedMonth, availableMonths = [], loading }) => {
  const [isOpen, setIsOpen] = useState(false);

  // Generate months dynamically based on available data or last 12 months
  const generateMonthOptions = () => {
    if (availableMonths && availableMonths.length > 0) {
      return availableMonths;
    }

    // Default: Generate last 12 months
    const months = [];
    for (let i = 0; i < 12; i++) {
      const date = moment().subtract(i, 'months');
      months.push({
        value: date.format('YYYY-MM'),
        label: date.format('MMMM YYYY'),
        month: date.month() + 1,
        year: date.year()
      });
    }
    return months;
  };

  const monthOptions = generateMonthOptions();
  const currentSelection = monthOptions.find(m => m.value === selectedMonth) || monthOptions[0];

  const handlePreviousMonth = () => {
    const currentIndex = monthOptions.findIndex(m => m.value === selectedMonth);
    if (currentIndex < monthOptions.length - 1) {
      onMonthSelect(monthOptions[currentIndex + 1].value);
    }
  };

  const handleNextMonth = () => {
    const currentIndex = monthOptions.findIndex(m => m.value === selectedMonth);
    if (currentIndex > 0) {
      onMonthSelect(monthOptions[currentIndex - 1].value);
    }
  };

  const canGoNext = monthOptions.findIndex(m => m.value === selectedMonth) > 0;
  const canGoPrevious = monthOptions.findIndex(m => m.value === selectedMonth) < monthOptions.length - 1;

  return (
    <div className="relative">
      {/* Desktop View */}
      <div className="hidden md:flex items-center space-x-2 bg-white rounded-lg shadow-sm border border-gray-200 p-2">
        <button
          onClick={handlePreviousMonth}
          disabled={!canGoPrevious || loading}
          className="p-1.5 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          aria-label="Previous month"
        >
          <ChevronLeft className="h-4 w-4 text-gray-600" />
        </button>

        <button
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center space-x-2 px-3 py-1.5 rounded hover:bg-gray-50 transition-colors min-w-[160px]"
          disabled={loading}
        >
          <Calendar className="h-4 w-4 text-gray-500" />
          <span className="font-medium text-gray-700">
            {currentSelection?.label || 'Select Month'}
          </span>
        </button>

        <button
          onClick={handleNextMonth}
          disabled={!canGoNext || loading}
          className="p-1.5 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          aria-label="Next month"
        >
          <ChevronRight className="h-4 w-4 text-gray-600" />
        </button>
      </div>

      {/* Mobile View */}
      <div className="md:hidden">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="w-full flex items-center justify-between px-4 py-2 bg-white border border-gray-200 rounded-lg shadow-sm"
          disabled={loading}
        >
          <div className="flex items-center space-x-2">
            <Calendar className="h-4 w-4 text-gray-500" />
            <span className="font-medium text-gray-700">
              {currentSelection?.label || 'Select Month'}
            </span>
          </div>
          <ChevronRight className={`h-4 w-4 text-gray-500 transform transition-transform ${isOpen ? 'rotate-90' : ''}`} />
        </button>
      </div>

      {/* Dropdown Menu */}
      {isOpen && (
        <>
          <div
            className="fixed inset-0 z-10"
            onClick={() => setIsOpen(false)}
          />
          <div className="absolute top-full mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-200 z-20 max-h-80 overflow-y-auto">
            <div className="p-2">
              {monthOptions.map((month) => (
                <button
                  key={month.value}
                  onClick={() => {
                    onMonthSelect(month.value);
                    setIsOpen(false);
                  }}
                  className={`w-full text-left px-3 py-2 rounded-md transition-colors ${
                    month.value === selectedMonth
                      ? 'bg-blue-50 text-blue-700 font-medium'
                      : 'hover:bg-gray-50 text-gray-700'
                  }`}
                  disabled={loading}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <span>{month.label}</span>
                      {month.count !== undefined && (
                        <div className="text-xs text-gray-500 mt-0.5">
                          {month.count} videos{month.acceptancePercentage !== undefined && ` • ${month.acceptancePercentage}% accepted`}
                        </div>
                      )}
                    </div>
                    {month.value === selectedMonth && (
                      <div className="w-2 h-2 bg-blue-600 rounded-full" />
                    )}
                  </div>
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default MonthSelector;